import { MouseEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@tellescope/design-system";
import { DestructiveConfirmDialog } from "@tellescope/ui";
import { formatDateTime, formatTime } from "@tellescope/utils";
import { Ban, Check, UserX } from "lucide-react";

export type CalendarListItemProps = {
  title: string;
  startTimeInMS: number;
  onOpen?: (() => void) | { to: string; replace?: boolean };
  onCancel: () => void;
  onNoShow: () => void;
  onComplete: () => void;
  className?: string;
};

export const CalendarListItem = ({
  title,
  startTimeInMS,
  onOpen,
  onCancel,
  onNoShow,
  onComplete,
  className: classNameProp,
}: CalendarListItemProps) => {
  const { t } = useTranslation("contacts");

  const Component = typeof onOpen === "function" ? "button" : typeof onOpen === "object" ? Link : "div";
  const interactive = onOpen !== undefined;

  const className = cn(
    "w-full text-left p-3 rounded-md border bg-white transition-colors block",
    interactive && "hover:bg-muted/50 cursor-pointer",
    classNameProp
  );

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!interactive) return;
    event.stopPropagation();
    if (typeof onOpen === "function") onOpen();
  };

  const handleAction = (event: MouseEvent<HTMLElement>, action: () => void) => {
    event.stopPropagation();
    event.preventDefault();
    action();
  };

  return (
    <Component
      to={typeof onOpen === "object" && typeof Component !== "string" ? onOpen.to : ""}
      replace={typeof onOpen === "object" && typeof Component !== "string" ? onOpen.replace : undefined}
      onClick={interactive ? handleClick : undefined}
      className={className}
      data-testid="contact-details-block-item"
    >
      <div className="flex justify-between items-center gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate text-sm">{title}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {formatDateTime(startTimeInMS, "MMM D, YYYY")} · {formatTime(startTimeInMS, "hh:mm a")}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Cancelling and marking a no-show change the appointment for the patient as well as the
              record, and neither is undoable from this list — so both are confirmed first. Marking
              complete is left immediate: it is benign and can be toggled back. */}
          <DestructiveConfirmDialog
            title={t("Cancel this appointment?")}
            description={t("This will cancel the appointment. This action cannot be undone.")}
            confirmText={t("Cancel appointment")}
            cancelText={t("Keep appointment")}
            onConfirm={onCancel}
          >
            <Button iconOnly variant="ghost" size="sm" color="muted" hoverLabel={t("Cancel")}>
              <Ban />
            </Button>
          </DestructiveConfirmDialog>
          <DestructiveConfirmDialog
            title={t("Mark as no show?")}
            description={t("This will mark the appointment as a no show. This action cannot be undone.")}
            confirmText={t("Mark as no show")}
            onConfirm={onNoShow}
          >
            <Button iconOnly variant="ghost" size="sm" color="muted" hoverLabel={t("No show")}>
              <UserX />
            </Button>
          </DestructiveConfirmDialog>
          <Button
            iconOnly
            variant="ghost"
            size="sm"
            color="muted"
            hoverLabel={t("Completed")}
            onClick={(event) => handleAction(event, onComplete)}
          >
            <Check />
          </Button>
        </div>
      </div>
    </Component>
  );
};
